<template>
  <div class="dossier-page">
    <div class="dossier-header">
      <div>
        <p class="eyebrow">Ressources humaines</p>
        <h1>Dossiers employés</h1>
      </div>
      <div class="search-box">
        <input v-model="searchQuery" type="search" placeholder="Rechercher un employé..." @input="searchStaff" />
      </div>
    </div>

    <div v-if="searchResults.length" class="result-list">
      <button v-for="staff in searchResults" :key="staff.Id" class="result-item" @click="selectStaff(staff.Id)">
        <img :src="staff.Pic ? staff.Pic : '/pointage/profile/profile.jpg'" :alt="staff.First_name" />
        <span>
          <strong>{{ staff.First_name }} {{ staff.Last_name }}</strong>
          <small>{{ staff.Post_name }}</small>
        </span>
        <em :class="{ stopped: staff.Is_stopped === 1 }">{{ staff.Is_stopped === 1 ? 'Arrêté' : 'Actif' }}</em>
      </button>
    </div>

    <div v-if="loading" class="state-card">Chargement du dossier...</div>
    <div v-else-if="!dossier" class="state-card">Recherchez un employé pour ouvrir son dossier RH.</div>

    <div v-else class="employee-dashboard">
      <aside class="employee-sidebar">
        <section class="identity-card">
          <img :src="staffPic" :alt="staffName" class="profile-photo" />
          <div>
            <span class="status-chip" :class="dossier.status">{{ dossier.status === 'arret' ? 'Arrêté' : 'Actif' }}</span>
            <h2>{{ staffName }}</h2>
            <p>{{ dossier.staff.Post_name }}</p>
          </div>
        </section>

        <section class="info-panel">
          <div class="panel-head compact">
            <div>
              <p class="eyebrow">Identité</p>
              <h3>Informations</h3>
            </div>
          </div>
          <div class="info-grid">
            <div><span>ID</span><strong>{{ dossier.staff.Id }}</strong></div>
            <div><span>Date début</span><strong>{{ formatDate(dossier.staff.Created_at) || '-' }}</strong></div>
            <div><span>Téléphone</span><strong>{{ dossier.staff.Phone || '-' }}</strong></div>
            <div><span>CIN</span><strong>{{ dossier.staff.Email || '-' }}</strong></div>
            <div class="wide"><span>Salaire actuel</span><strong>{{ dossier.staff.Salary || 0 }} DH</strong></div>
          </div>
        </section>
      </aside>

      <main class="employee-main">
        <section class="action-panel">
          <div class="panel-head">
            <div>
              <p class="eyebrow">Statut RH</p>
              <h3>{{ dossier.status === 'arret' ? 'Retour de l’employé' : 'Nouvel arrêt' }}</h3>
            </div>
            <span class="panel-status" :class="dossier.status">{{ dossier.status === 'arret' ? 'Arrêt en cours' : 'Disponible' }}</span>
          </div>

          <div v-if="dossier.status === 'arret'" class="rh-form">
            <div class="active-stop">
              <span>Arrêt depuis</span>
              <strong>{{ formatDate(dossier.active_stop.start_date) }}</strong>
              <p v-if="dossier.active_stop.motif">{{ dossier.active_stop.motif }}</p>
            </div>
            <div class="form-row">
              <label>
                Date de retour
                <input v-model="returnDate" type="date" class="form-input" />
              </label>
              <button class="btn btn-success action-submit" @click="returnStaff">Valider le retour</button>
            </div>
          </div>

          <div v-else class="rh-form">
            <div class="form-row">
              <label>
                Date d’arrêt
                <input v-model="stopDate" type="date" class="form-input" />
              </label>
              <label>
                Motif
                <textarea v-model="stopMotif" class="form-input" placeholder="Motif RH"></textarea>
              </label>
            </div>
            <div class="action-footer">
              <button class="btn btn-danger action-submit" @click="stopStaff">Enregistrer l’arrêt</button>
            </div>
          </div>
        </section>

        <section class="history-panel">
          <div class="panel-head">
            <div>
              <p class="eyebrow">Historique</p>
              <h3>Événements RH</h3>
            </div>
            <div class="period-controls">
              <select v-model="periodMode">
                <option value="month">Par mois</option>
                <option value="range">Du / Au</option>
              </select>
              <MonthPickerButton v-if="periodMode === 'month'" v-model="selectedMonth" />
              <template v-else>
                <input v-model="periodStart" type="date" />
                <input v-model="periodEnd" type="date" />
              </template>
              <button @click="reloadPeriod">Afficher</button>
            </div>
          </div>

          <div class="rh-summary-grid">
            <div>
              <span>Absences</span>
              <strong>{{ eventSummary.absences }}</strong>
            </div>
            <div>
              <span>Congés</span>
              <strong>{{ eventSummary.conges }}</strong>
            </div>
            <div>
              <span>Repos</span>
              <strong>{{ eventSummary.repos }}</strong>
            </div>
            <div>
              <span>Arrêts</span>
              <strong>{{ eventSummary.arrets }}</strong>
            </div>
          </div>

          <div class="history-tools">
            <div class="filter-pills">
              <button :class="{ active: eventFilter === 'all' }" @click="eventFilter = 'all'">Tous</button>
              <button :class="{ active: eventFilter === 'absence' }" @click="eventFilter = 'absence'">Absences</button>
              <button :class="{ active: eventFilter === 'conge' }" @click="eventFilter = 'conge'">Congés</button>
              <button :class="{ active: eventFilter === 'repos' }" @click="eventFilter = 'repos'">Repos</button>
              <button :class="{ active: eventFilter === 'arret' }" @click="eventFilter = 'arret'">Arrêts</button>
            </div>
            <label class="toggle-detail">
              <input v-model="showAutoDetails" type="checkbox" />
              Détail auto
            </label>
          </div>

          <div v-if="!visibleEvents.length && !autoAbsenceGroups.length" class="empty-history">Aucun événement RH.</div>
          <div v-else class="history-table">
            <div class="history-table-head">
              <span>Type</span>
              <span>Période</span>
              <span>Origine</span>
              <span>Motif</span>
            </div>

            <div v-for="group in autoAbsenceGroups" :key="group.key" class="history-row grouped-row">
              <div>
                <span class="event-type absence_non_justifiee">Absences automatiques</span>
              </div>
              <strong>{{ group.title }}</strong>
              <span>Auto</span>
              <span>{{ group.count }} jour{{ group.count > 1 ? 's' : '' }}</span>
            </div>

            <div v-for="event in visibleEvents" :key="event.id" class="history-row">
              <div>
                <span class="event-type" :class="event.event_type">{{ eventLabel(event) }}</span>
              </div>
              <strong>{{ formatDate(event.start_date) }}<template v-if="event.end_date"> → {{ formatDate(event.end_date) }}</template></strong>
              <span>{{ event.source === 'auto' ? 'Auto' : 'Manuel' }}</span>
              <span>{{ event.motif || '-' }}</span>
            </div>
          </div>
        </section>

        <section class="salary-panel">
          <div class="panel-head">
            <div>
              <p class="eyebrow">Salaire</p>
              <h3>Historique salaire</h3>
            </div>
            <span class="history-count">{{ salaryHistory.length }}</span>
          </div>

          <div v-if="!salaryHistory.length" class="empty-history">Aucun historique salaire.</div>
          <div v-else class="salary-list">
            <div v-for="item in salaryHistory" :key="item.id || item.effective_from" class="salary-row">
              <span>Depuis {{ formatDate(item.effective_from) }}</span>
              <strong>{{ formatAmount(item.salary) }} DH</strong>
            </div>
          </div>
        </section>

        <section class="salary-panel">
          <div class="panel-head">
            <div>
              <p class="eyebrow">Poste</p>
              <h3>Historique poste</h3>
            </div>
            <span class="history-count">{{ postHistory.length }}</span>
          </div>

          <div v-if="!postHistory.length" class="empty-history">Aucun historique poste.</div>
          <div v-else class="salary-list">
            <div v-for="item in postHistory" :key="item.id || item.effective_from" class="salary-row">
              <span>Depuis {{ formatDate(item.effective_from) }}</span>
              <strong>{{ item.post_name }}</strong>
            </div>
          </div>
        </section>

        <section class="salary-panel">
          <div class="panel-head">
            <div>
              <p class="eyebrow">Groupe horaire</p>
              <h3>Historique groupe</h3>
            </div>
            <span class="history-count">{{ groupHistory.length }}</span>
          </div>

          <div v-if="!groupHistory.length" class="empty-history">Aucun historique groupe.</div>
          <div v-else class="salary-list">
            <div v-for="item in groupHistory" :key="item.id || item.start_date" class="salary-row">
              <span>{{ groupPeriodLabel(item) }}</span>
              <strong>{{ item.group_name }}</strong>
            </div>
          </div>
        </section>
      </main>
    </div>
  </div>
</template>

<script>
import MonthPickerButton from './MonthPickerButton.vue';

export default {
  name: 'AppEmployeeDossier',
  components: { MonthPickerButton },
  data() {
    return {
      searchQuery: '',
      searchResults: [],
      dossier: null,
      loading: false,
      stopDate: this.todayForInput(),
      returnDate: this.todayForInput(),
      stopMotif: '',
      searchTimer: null,
      eventFilter: 'all',
      showAutoDetails: false,
      periodMode: 'month',
      selectedMonth: new Date().toISOString().slice(0, 7),
      periodStart: this.firstDayOfCurrentMonth(),
      periodEnd: this.lastDayOfCurrentMonth(),
    };
  },
  computed: {
    staffName() {
      if (!this.dossier) return '';
      return `${this.dossier.staff.First_name} ${this.dossier.staff.Last_name || ''}`.trim();
    },
    staffPic() {
      return this.dossier?.staff?.Pic || '/pointage/profile/profile.jpg';
    },
    salaryHistory() {
      return this.dossier?.salary_history || [];
    },
    postHistory() {
      return this.dossier?.post_history || [];
    },
    groupHistory() {
      return this.dossier?.group_history || [];
    },
    eventSummary() {
      const summary = { absences: 0, conges: 0, repos: 0, arrets: 0 };
      (this.dossier?.events || []).forEach((event) => {
        if (event.event_type && event.event_type.includes('absence')) summary.absences += 1;
        if (event.event_type && event.event_type.includes('conge')) summary.conges += 1;
        if (event.event_type === 'repos') summary.repos += 1;
        if (event.event_type === 'arret') summary.arrets += 1;
      });
      return summary;
    },
    filteredEvents() {
      const events = this.dossier?.events || [];
      return events.filter((event) => {
        if (this.eventFilter === 'all') return true;
        if (this.eventFilter === 'absence') return event.event_type && event.event_type.includes('absence');
        if (this.eventFilter === 'conge') return event.event_type && event.event_type.includes('conge');
        return event.event_type === this.eventFilter;
      });
    },
    visibleEvents() {
      if (this.showAutoDetails) return this.filteredEvents;
      return this.filteredEvents.filter((event) => !(event.source === 'auto' && event.event_type === 'absence_non_justifiee'));
    },
    autoAbsenceGroups() {
      if (this.showAutoDetails || (this.eventFilter !== 'all' && this.eventFilter !== 'absence')) return [];
      const groups = new Map();
      this.filteredEvents
        .filter((event) => event.source === 'auto' && event.event_type === 'absence_non_justifiee')
        .forEach((event) => {
          const key = String(event.start_date || '').slice(0, 7);
          if (!key) return;
          const group = groups.get(key) || { key, count: 0, dates: [] };
          group.count += 1;
          group.dates.push(event.start_date);
          groups.set(key, group);
        });
      return Array.from(groups.values()).map((group) => ({
        ...group,
        title: this.monthLabel(group.key),
      }));
    },
  },
  mounted() {
    const id = this.$route.query.id;
    if (id) this.fetchDossier(id);
  },
  watch: {
    '$route.query.id'(id) {
      if (id) this.fetchDossier(id);
    },
  },
  methods: {
    todayForInput() {
      const now = new Date();
      const offsetMs = now.getTimezoneOffset() * 60000;
      return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
    },
    firstDayOfCurrentMonth() {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    },
    lastDayOfCurrentMonth() {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
    },
    async searchStaff() {
      clearTimeout(this.searchTimer);
      const query = this.searchQuery.trim();
      if (query.length < 2) {
        this.searchResults = [];
        return;
      }
      this.searchTimer = setTimeout(async () => {
        const res = await fetch(`/api-pointage/search?query=${encodeURIComponent(query)}`, { cache: 'no-store' });
        this.searchResults = res.ok ? await res.json() : [];
      }, 220);
    },
    selectStaff(id) {
      this.searchResults = [];
      this.$router.push({ path: '/dossiers', query: { id } });
    },
    async fetchDossier(id) {
      this.loading = true;
      try {
        const params = new URLSearchParams({ id: String(id) });
        if (this.periodMode === 'month') {
          params.set('month', this.selectedMonth);
        } else {
          params.set('start', this.periodStart);
          params.set('end', this.periodEnd);
        }
        const res = await fetch(`/api-pointage/staff-dossier?${params.toString()}`, { cache: 'no-store' });
        if (!res.ok) {
          const err = await res.json().catch(() => null);
          throw new Error(err?.error || 'Erreur dossier');
        }
        this.dossier = await res.json();
        this.stopDate = this.todayForInput();
        this.returnDate = this.todayForInput();
        this.stopMotif = '';
      } catch (err) {
        alert(err.message);
        this.dossier = null;
      } finally {
        this.loading = false;
      }
    },
    reloadPeriod() {
      if (!this.dossier) return;
      this.fetchDossier(this.dossier.staff.Id);
    },
    async stopStaff() {
      if (!this.dossier) return;
      if (!confirm(`Arrêter ${this.staffName} à partir du ${this.formatDate(this.stopDate)} ?`)) return;
      await this.postRH('/api-pointage/rh-stop', {
        staff_id: this.dossier.staff.Id,
        start_date: this.stopDate,
        motif: this.stopMotif,
      });
    },
    async returnStaff() {
      if (!this.dossier) return;
      if (!confirm(`Faire revenir ${this.staffName} le ${this.formatDate(this.returnDate)} ?`)) return;
      await this.postRH('/api-pointage/rh-return', {
        staff_id: this.dossier.staff.Id,
        return_date: this.returnDate,
      });
    },
    async postRH(url, payload) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        alert(err?.error || 'Erreur RH');
        return;
      }
      await this.fetchDossier(this.dossier.staff.Id);
    },
    eventLabel(event) {
      const labels = {
        arret: 'Arrêt',
        repos: 'Repos',
        conge_paye: 'Congé payé',
        conge_non_paye: 'Congé non payé',
        maladie: 'Maladie',
        absence_justifiee: 'Absence justifiée',
        absence_non_justifiee: 'Absence non justifiée',
      };
      return labels[event.event_type] || event.label || event.event_type;
    },
    formatDate(value) {
      if (!value) return '';
      const [year, month, day] = String(value).replace(/\//g, '-').slice(0, 10).split('-');
      return `${day}/${month}/${year}`;
    },
    formatAmount(value) {
      const amount = Number(value || 0);
      return amount.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    groupPeriodLabel(item) {
      const start = this.formatDate(item.start_date);
      if (!item.end_date) return `Depuis ${start}`;
      return `${start} -> ${this.formatDate(item.end_date)}`;
    },
    monthLabel(value) {
      const [year, month] = String(value).split('-').map(Number);
      if (!year || !month) return value;
      return new Date(year, month - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    },
  },
};
</script>

<style scoped>
.dossier-page {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.dossier-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
}

.eyebrow {
  margin: 0 0 4px;
  color: var(--primary);
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.dossier-header h1,
.identity-card h2,
.panel-head h3 {
  margin: 0;
  color: var(--gray-900);
}

.dossier-header h1 {
  font-size: 24px;
}

.search-box {
  width: min(360px, 100%);
}

.search-box input,
.form-input {
  width: 100%;
  min-height: 40px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--white);
  color: var(--gray-800);
  padding: 0 12px;
}

textarea.form-input {
  min-height: 88px;
  padding: 10px 12px;
  resize: vertical;
}

.result-list,
.state-card,
.identity-card,
.info-panel,
.action-panel,
.history-panel,
.salary-panel {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: var(--shadow-sm);
}

.result-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 8px;
  padding: 10px;
}

.result-item {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  min-height: 62px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #f8fafc;
  padding: 8px;
  cursor: pointer;
  text-align: left;
}

.result-item img {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  object-fit: cover;
}

.result-item strong,
.result-item small {
  display: block;
}

.result-item small,
.result-item em {
  color: var(--gray-500);
  font-size: 12px;
}

.result-item em {
  font-style: normal;
  font-weight: 800;
}

.result-item em.stopped {
  color: var(--danger);
}

.state-card {
  padding: 18px;
  text-align: center;
  color: var(--gray-500);
}

.employee-dashboard {
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
  gap: 14px;
  align-items: start;
}

.employee-sidebar,
.employee-main {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.identity-card,
.info-panel,
.action-panel,
.history-panel,
.salary-panel {
  padding: 15px;
}

.identity-card {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  align-items: center;
  gap: 14px;
}

.profile-photo {
  width: 72px;
  height: 72px;
  border-radius: 12px;
  object-fit: cover;
  border: 1px solid var(--border);
}

.identity-card h2 {
  margin-top: 8px;
  font-size: 19px;
}

.identity-card p {
  margin: 4px 0 0;
  color: var(--gray-500);
  font-weight: 700;
}

.status-chip,
.panel-status,
.history-count,
.event-type {
  display: inline-flex;
  align-items: center;
  width: max-content;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
}

.status-chip {
  padding: 5px 10px;
  background: #dcfce7;
  color: #166534;
}

.status-chip.arret,
.panel-status.arret {
  background: #fee2e2;
  color: #991b1b;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.info-grid div {
  min-height: 58px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #f8fafc;
  padding: 10px;
}

.info-grid div.wide {
  grid-column: 1 / -1;
}

.info-grid span,
.rh-form label {
  display: block;
  color: var(--gray-500);
  font-size: 12px;
  font-weight: 800;
}

.info-grid strong {
  display: block;
  margin-top: 6px;
  color: var(--gray-900);
  font-size: 14px;
}

.panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.panel-head.compact {
  margin-bottom: 12px;
}

.panel-status,
.history-count {
  padding: 5px 10px;
  background: var(--gray-100);
  color: var(--gray-600);
}

.period-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.period-controls select,
.period-controls input,
.period-controls button {
  min-height: 36px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fff;
  color: var(--gray-800);
  padding: 0 10px;
  font: inherit;
  font-size: 13px;
  font-weight: 800;
}

.period-controls button {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
  cursor: pointer;
}

.rh-form {
  display: grid;
  gap: 14px;
}

.form-row {
  display: grid;
  grid-template-columns: minmax(180px, 260px) minmax(0, 1fr);
  gap: 12px;
  align-items: end;
}

.rh-form label {
  display: grid;
  gap: 6px;
}

.active-stop {
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fef2f2;
  color: #991b1b;
  padding: 12px;
}

.active-stop span,
.active-stop strong,
.active-stop p {
  display: block;
  margin: 0;
}

.active-stop span {
  font-size: 12px;
  font-weight: 800;
}

.active-stop strong {
  margin-top: 4px;
}

.active-stop p {
  margin-top: 6px;
  color: #7f1d1d;
}

.action-footer {
  display: flex;
  justify-content: flex-end;
}

.action-submit {
  min-height: 42px;
  width: max-content;
  min-width: 180px;
  border-radius: 8px;
  padding: 0 18px;
  font-weight: 800;
}

.btn-danger {
  background: #ef4444;
  color: #fff;
  border: 1px solid #ef4444;
}

.btn-success {
  background: #16a34a;
  color: #fff;
  border: 1px solid #16a34a;
}

.empty-history {
  border: 1px dashed var(--border);
  border-radius: 8px;
  padding: 18px;
  color: var(--gray-500);
  text-align: center;
}

.salary-list {
  display: grid;
  gap: 8px;
}

.salary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 46px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #f8fafc;
  padding: 10px 12px;
}

.salary-row span {
  color: var(--gray-500);
  font-size: 13px;
  font-weight: 700;
}

.salary-row strong {
  color: var(--gray-900);
  font-size: 15px;
}

.event-type {
  padding: 4px 9px;
  background: #eef2ff;
  color: #3730a3;
}

.event-type.arret {
  background: #fee2e2;
  color: #991b1b;
}

.event-type.absence_non_justifiee {
  background: #fef2f2;
  color: #b91c1c;
}

.event-type.absence_justifiee {
  background: #fff7ed;
  color: #c2410c;
}

.event-type.conge_paye,
.event-type.conge_non_paye,
.event-type.maladie {
  background: #ccfbf1;
  color: #0f766e;
}

.rh-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}

.rh-summary-grid div {
  min-height: 66px;
  border: 1px solid #e5eaf5;
  border-radius: 8px;
  background: #f8fbff;
  padding: 10px 12px;
}

.rh-summary-grid span {
  display: block;
  color: var(--gray-500);
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
}

.rh-summary-grid strong {
  display: block;
  margin-top: 8px;
  color: var(--gray-900);
  font-size: 22px;
}

.history-tools {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.filter-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.filter-pills button {
  min-height: 32px;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: #fff;
  color: var(--gray-600);
  padding: 0 12px;
  font-weight: 800;
  cursor: pointer;
}

.filter-pills button.active {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}

.toggle-detail {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  color: var(--gray-600);
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
}

.history-table {
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 8px;
}

.history-table-head,
.history-row {
  display: grid;
  grid-template-columns: 190px 180px 90px minmax(0, 1fr) 100px;
  align-items: center;
  gap: 12px;
}

.history-table-head {
  min-height: 42px;
  padding: 0 12px;
  background: #f8fafc;
  border-bottom: 1px solid var(--border);
  color: var(--gray-500);
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
}

.history-row {
  min-height: 58px;
  padding: 10px 12px;
  background: #fff;
  border-bottom: 1px solid #edf1f8;
  color: var(--gray-700);
  font-size: 13px;
  font-weight: 700;
}

.history-row:last-child {
  border-bottom: 0;
}

.history-row strong {
  color: var(--gray-900);
}

.grouped-row {
  background: #fbfdff;
}

@media (max-width: 980px) {
  .employee-dashboard {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .dossier-header {
    align-items: stretch;
    flex-direction: column;
  }

  .search-box {
    width: 100%;
  }

  .identity-card,
  .form-row,
  .salary-row {
    grid-template-columns: 1fr;
  }

  .salary-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .profile-photo {
    width: 74px;
    height: 74px;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .action-footer,
  .action-submit {
    width: 100%;
  }

  .rh-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .history-tools {
    align-items: flex-start;
    flex-direction: column;
  }

  .period-controls {
    display: grid;
    grid-template-columns: 1fr;
    width: 100%;
  }

  .history-table {
    border: 0;
    display: grid;
    gap: 10px;
  }

  .history-table-head {
    display: none;
  }

  .history-row {
    grid-template-columns: 1fr;
    gap: 7px;
    border: 1px solid var(--border);
    border-radius: 8px;
  }

}
</style>
